import type {
  DomainOrderPreviewData,
  ExternalCertificateOrderPreviewData,
  HostingOrderPreviewData,
  OrderPreviewData,
} from "./types.js";
import { Money } from "../../base/Money.js";
import { DataModel } from "../../base/index.js";

export class OrderPreviewBase<T extends OrderPreviewData> extends DataModel<T> {
  public readonly totalPrice: Money;

  public constructor(data: T) {
    super(data);
    this.totalPrice = Money({ amount: data.totalPrice, currency: "EUR" });
  }
}

export class DomainOrderPreview extends OrderPreviewBase<DomainOrderPreviewData> {
  public readonly type = "domain";
  public readonly domainPrice: Money;
  public readonly feePrice: Money;
  public readonly domainContractDuration: number;

  public constructor(data: DomainOrderPreviewData) {
    super(data);
    this.domainPrice = Money({ amount: data.domainPrice, currency: "EUR" });
    this.feePrice = Money({ amount: data.feePrice, currency: "EUR" });
    this.domainContractDuration = data.domainContractDuration;
  }
}

export class HostingOrderPreview extends OrderPreviewBase<HostingOrderPreviewData> {
  public readonly type = "hosting";
  public readonly storagePrice: Money;
  public readonly machineTypePrice: Money;

  public constructor(data: HostingOrderPreviewData) {
    super(data);
    this.storagePrice = Money({ amount: data.storagePrice, currency: "EUR" });
    this.machineTypePrice = Money({
      amount: data.machineTypePrice,
      currency: "EUR",
    });
  }
}

export class ExternalCertificateOrderPreview extends OrderPreviewBase<ExternalCertificateOrderPreviewData> {
  public readonly type = "externalCertificate";
  public readonly feePrice: Money;
  public readonly recurringPrice: Money;

  public constructor(data: ExternalCertificateOrderPreviewData) {
    super(data);
    this.feePrice = Money({ amount: data.feePrice, currency: "EUR" });
    this.recurringPrice = Money({
      amount: data.recurringPrice,
      currency: "EUR",
    });
  }
}

export type OrderPreview =
  | DomainOrderPreview
  | HostingOrderPreview
  | ExternalCertificateOrderPreview;

export const orderPreviewFactory = (data: OrderPreviewData) => {
  if ("domainPrice" in data && "feePrice" in data) {
    return new DomainOrderPreview(data);
  }
  if ("storagePrice" in data && "machineTypePrice" in data) {
    return new HostingOrderPreview(data);
  }
  if ("recurringPrice" in data && "feePrice" in data) {
    return new ExternalCertificateOrderPreview(data);
  }

  throw new Error("Order preview type is not supported.");
};
